import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import btnClose from '../../images/common/close.svg';
import {
  Main,
  Header,
  Contents,
  CloseButton,
  CloseButtonImage,
  DeleteButton,
  UpdateButton,
  CancelButton,
} from '../../styles/common/Dialog';
import {
  Dialog,
  BodyBlock,
  EnhancedItemWrapper,
  InfoBody,
  StyledSelect,
  InfoTitle,
  ButtonsWrapper,
  BoldText,
  InputWrapper,
} from './styles/GroupDetailDialogStyles';
import UserTable from './UserTable';
import CustomInput from '../Parts/CustomInput';
import { convertISOFormatToDate, isCompanyAdmin } from '../../lib/tools';
import updateGroup from '../../actions/api/group/updateGroup';
import ConfirmationDialog from '../Parts/ConfirmationDialog';
import getGroup from '../../actions/api/group/getGroup';
import { checkAdminUser } from './functions/GroupDialogUtils';

// TODO: i18n対応
/**
 * グループ詳細ダイアログを表示するコンポーネント
 * 選択されたグループの詳細情報を表示し、編集または削除することができる
 *
 * ダイアログには、グループ名、タグセット、登録日時、最終更新日時、担当者のリストが表示されます。
 * 編集モードでは、これらの情報を変更することができ、更新ボタンで変更を保存します。
 * 削除ボタンを使用してグループを削除することもできます。
 *
 * @param {Object} selectedGroup 選択されたグループの詳細情報を含むオブジェクト
 * @param {Object[]} allTagSets 企業に紐づく全てのタグセットの配列
 * @param {function} onClose ダイアログを閉じるための関数。
 */
const GroupDetailDialog = ({ selectedGroup, allTagSets, onClose }) => {
  const dispatch = useDispatch();
  const apiToken = useSelector(state => state.main.apiToken);
  const companyAllUsers = useSelector(state => state.group.operators);
  const currentUserRole = useSelector(state => state.main.innerRole);
  const intl = useIntl();
  const { formatMessage } = intl;

  const isCompanyAdminUser = isCompanyAdmin(currentUserRole);

  const {
    id: selectedGroupId = '',
    name: selectedGroupName = '',
    tagSet: selectedGroupTagSet = {},
    createdAt: selectedGroupCreatedAt = '',
    updatedAt: selectedGroupUpdatedAt = '',
    members: selectedGroupMembers = [],
    isUsable: isGroupUsable,
    isDefault,
  } = selectedGroup || {};

  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
  const [showCancelAlertDialog, setShowCancelAlertDialog] = useState(false);
  const [inputText, setInputText] = useState(selectedGroupName);
  const [selectedTagSet, setSelectedTagSet] = useState();

  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const handleSelectedMembersChange = userIds => {
    setSelectedUserIds(userIds);
  };

  const handleEditButtonClick = () => {
    setIsEditMode(true);
  };

  const handleCancelButtonClick = () => {
    setIsEditMode(false);
    setInputText(selectedGroupName);
    setShowCancelAlertDialog(false);
  };

  const handleDeleteButtonClick = () => {
    dispatch(
      updateGroup(apiToken, selectedGroupId, {
        isUsable: false,
      }),
    );
    setShowDeleteAlertDialog(false);
    onClose();
  };

  const handleUpdateButtonClick = () => {
    if (
      !checkAdminUser(selectedUserIds, companyAllUsers, dispatch, formatMessage)
    ) {
      return;
    }

    dispatch(
      updateGroup(apiToken, selectedGroupId, {
        name: inputText,
        tagSetId: selectedTagSet,
        members: selectedUserIds,
      }),
    ).then(() => {
      // 更新操作が成功した後に詳細情報を取得し画面を更新
      dispatch(getGroup(apiToken, {}, selectedGroupId));
    });

    setIsEditMode(false);
  };

  useEffect(() => {
    if (selectedGroup && selectedGroup.tagSet) {
      setSelectedTagSet(selectedGroup.tagSet.id);
    }
  }, [selectedGroup]);

  return (
    <>
      <Main
        id="closeUpdateDeviceDialog"
        role="presentation"
        onClick={() => onClose()}
      >
        <Dialog role="presentation" onClick={e => e.stopPropagation()}>
          <Contents>
            <Header>
              {isEditMode ? (
                <div>グループ編集</div>
              ) : (
                <FormattedMessage id="group.dialog.title" />
              )}
              <CloseButton
                id="closeButton"
                role="presentation"
                className="button"
                onClick={() => onClose()}
              >
                <CloseButtonImage src={btnClose} alt="close" />
              </CloseButton>
            </Header>
            <BodyBlock>
              <EnhancedItemWrapper>
                {!isEditMode || !isCompanyAdminUser ? (
                  <>
                    <InfoTitle>
                      <FormattedMessage id="group.list.header.name" />
                    </InfoTitle>
                    <InfoBody>{selectedGroupName}</InfoBody>
                  </>
                ) : (
                  <>
                    <InfoTitle>
                      <FormattedMessage id="group.list.header.name" />
                    </InfoTitle>
                    <InfoBody>
                      <InputWrapper>
                        <CustomInput
                          value={inputText}
                          placeholder="グループ名"
                          onChange={e => setInputText(e.target.value)}
                          maxLength={30}
                        />
                      </InputWrapper>
                    </InfoBody>
                  </>
                )}
              </EnhancedItemWrapper>
              <EnhancedItemWrapper>
                {!isEditMode || !isCompanyAdminUser ? (
                  <>
                    <InfoTitle>タグセット</InfoTitle>
                    <InfoBody>{selectedGroupTagSet.name}</InfoBody>
                  </>
                ) : (
                  <>
                    <InfoTitle>タグセット</InfoTitle>
                    <InfoBody>
                      <StyledSelect
                        id="selectTagSet"
                        defaultValue={selectedGroupTagSet.id}
                        onChange={e => {
                          setSelectedTagSet(e.target.value);
                        }}
                      >
                        {allTagSets.map(eachTagSet => (
                          <option key={eachTagSet.id} value={eachTagSet.id}>
                            {eachTagSet.name}
                          </option>
                        ))}
                      </StyledSelect>
                    </InfoBody>
                  </>
                )}
              </EnhancedItemWrapper>
              {!isEditMode && (
                <>
                  <EnhancedItemWrapper>
                    <InfoTitle>登録日時</InfoTitle>
                    <InfoBody>
                      {convertISOFormatToDate(selectedGroupCreatedAt)}
                    </InfoBody>
                  </EnhancedItemWrapper>
                  <EnhancedItemWrapper>
                    <InfoTitle>最終更新日時</InfoTitle>
                    <InfoBody>
                      {convertISOFormatToDate(selectedGroupUpdatedAt)}
                    </InfoBody>
                  </EnhancedItemWrapper>
                </>
              )}
              <EnhancedItemWrapper>
                <InfoTitle>担当者</InfoTitle>
                <UserTable
                  members={selectedGroupMembers}
                  isEditMode={isEditMode}
                  onSelectedMembersChange={handleSelectedMembersChange}
                />
              </EnhancedItemWrapper>
            </BodyBlock>
            {isGroupUsable && (
              <ButtonsWrapper>
                {!isEditMode ? (
                  <>
                    {isCompanyAdminUser && !isDefault && (
                      <DeleteButton
                        onClick={() => setShowDeleteAlertDialog(true)}
                      >
                        削除
                      </DeleteButton>
                    )}
                    <UpdateButton onClick={handleEditButtonClick}>
                      編集
                    </UpdateButton>
                  </>
                ) : (
                  <>
                    <CancelButton
                      onClick={() => setShowCancelAlertDialog(true)}
                    >
                      キャンセル
                    </CancelButton>
                    <UpdateButton onClick={handleUpdateButtonClick}>
                      更新
                    </UpdateButton>
                  </>
                )}
              </ButtonsWrapper>
            )}
          </Contents>
        </Dialog>
      </Main>
      {showCancelAlertDialog && (
        <ConfirmationDialog
          headerText="グループ編集"
          messageText={
            <>
              グループ編集をキャンセルします。
              <br />
              <br />
              グループ編集をキャンセルすると編集内容は破棄されますがよろしいですか。
            </>
          }
          onCancel={() => setShowCancelAlertDialog(false)}
          onConfirm={handleCancelButtonClick}
          confirmButtonText="編集内容を破棄"
        />
      )}
      {showDeleteAlertDialog && (
        <ConfirmationDialog
          headerText="グループ削除"
          messageText={
            <>
              <BoldText>{selectedGroupName}</BoldText>グループを削除します。
              <br />
              <br />
              ※グループを削除すると紐づくデータにも影響が及びます。
            </>
          }
          onCancel={() => setShowDeleteAlertDialog(false)}
          onConfirm={handleDeleteButtonClick}
        />
      )}
    </>
  );
};

GroupDetailDialog.propTypes = {
  selectedGroup: PropTypes.object.isRequired,
  allTagSets: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default GroupDetailDialog;
